import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Baby, Clock, UtensilsCrossed, Award, ShieldCheck, Blocks, Apple, DoorOpen,
  ArrowRight, ArrowUpRight, Quote, ChevronLeft, ChevronRight, Phone, Sparkles,
} from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { TourForm } from "../components/TourForm";
import { API_URL, IMAGES, SITE } from "../data/content";
import { useLang } from "../i18n";

const ICONS = { Baby, Clock, UtensilsCrossed, Award, ShieldCheck, Blocks, Apple, DoorOpen };
const FACT_COLORS = [
  { bg: "#FDE7E7", fg: "#D7352B" },
  { bg: "#FFF2C4", fg: "#B98A00" },
  { bg: "#E3EFFC", fg: "#2E7CD6" },
  { bg: "#E6F4EA", fg: "#3D9B4F" },
];
const CHAPTER_COLORS = ["#FFC400", "#FF6B6B", "#6FB1FF", "#7ED07E"];
const CONFETTI = [
  { color: "#D7352B", top: "4%", left: "-2%" },
  { color: "#FFC400", top: "78%", left: "96%" },
  { color: "#2E7CD6", top: "88%", left: "6%" },
  { color: "#43A047", top: "12%", left: "94%" },
];

const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[92vh] flex items-end overflow-hidden bg-forest">
      <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.building}
          alt="The Learning Tree Academy school building on Emerson Drive, Palm Bay"
          className="w-full h-full object-cover scale-110 object-[50%_58%] sm:object-[16%_72%]"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to top, rgba(28,61,110,0.96) 0%, rgba(28,61,110,0.82) 34%, rgba(28,61,110,0.15) 62%, rgba(28,61,110,0) 100%)" }}
        />
        <div
          className="absolute inset-y-0 left-0 w-2/3"
          style={{ background: "linear-gradient(to right, rgba(28,61,110,0.55) 0%, rgba(28,61,110,0) 100%)" }}
        />
      </motion.div>

      {CONFETTI.map((cDot, i) => (
        <span
          key={cDot.color}
          className="absolute w-4 h-4 rounded-full animate-float-slow z-10"
          style={{ background: cDot.color, top: cDot.top, left: cDot.left, animationDelay: `${i * 0.9}s` }}
        />
      ))}

      <div data-testid="accreditation-badge" className="animate-float-slow absolute right-4 sm:right-10 top-28 sm:top-32 bg-white rounded-2xl shadow-xl shadow-black/20 px-4 py-3 flex items-center gap-3 border border-bone z-20">
        <img src={IMAGES.necpa} alt="NECPA" className="h-8 w-auto" />
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-bark/60">Gold Seal</p>
          <p className="text-xs font-display font-bold text-forest">NECPA Accredited</p>
        </div>
      </div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full relative z-10 pb-16 sm:pb-20 pt-40">
        <div className="max-w-3xl">
          <MaskedLine delay={0.1}>
            <span data-testid="hero-badge" className="inline-flex items-center gap-2 rounded-full bg-amber px-4 py-1.5 text-[11px] font-bold tracking-[0.18em] uppercase text-forest">
              <Sparkles className="w-3.5 h-3.5 text-tangerine" /> {t("hero_badge")}
            </span>
          </MaskedLine>

          <h1 data-testid="hero-headline" className="mt-6 font-display font-extrabold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl text-white drop-shadow-lg">
            <MaskedLine delay={0.25}>{t("hero_l1")}</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="relative inline-block">
                {t("hero_l2")}
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none" preserveAspectRatio="none">
                  <motion.path
                    d="M3 9 C 60 2, 140 2, 197 8"
                    stroke="#FFC400" strokeWidth="6" strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.8, delay: 1.1, ease: "easeOut" }}
                  />
                </svg>
              </span>
            </MaskedLine>
            <MaskedLine delay={0.55}>{t("hero_l3")}</MaskedLine>
          </h1>

          <MaskedLine delay={0.75}>
            <p data-testid="hero-subtitle" className="mt-7 text-base sm:text-lg text-white leading-relaxed max-w-xl [text-shadow:0_1px_8px_rgba(15,42,32,0.7)]">{t("hero_sub")}</p>
          </MaskedLine>

          <MaskedLine delay={0.9}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-book-tour-btn"
                onClick={() => document.getElementById("book-tour")?.scrollIntoView({ behavior: "smooth" })}
                className="group rounded-full bg-tangerine text-white px-8 py-4 font-display font-bold flex items-center gap-2 hover:bg-clay transition-colors duration-300 shadow-xl shadow-black/30"
              >
                {t("hero_book")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                data-testid="hero-call-btn"
                href={SITE.phoneHref}
                className="rounded-full border-2 border-white/50 px-8 py-4 font-display font-bold text-white flex items-center gap-2 hover:border-amber hover:text-amber transition-colors duration-300 backdrop-blur-sm"
              >
                <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
              </a>
            </div>
          </MaskedLine>

          <MaskedLine delay={1.05}>
            <div data-testid="hero-stats" className="mt-12 flex items-center gap-8 sm:gap-10">
              {[
                ["30+", t("stat_years"), "#FFC400"],
                ["1–12", t("stat_ages"), "#6FB1FF"],
                ["3", t("stat_meals"), "#7ED07E"],
              ].map(([v, l, color]) => (
                <div key={l}>
                  <p className="font-accent font-semibold text-3xl sm:text-4xl" style={{ color }}>{v}</p>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-cream/70 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </MaskedLine>
        </div>
      </motion.div>
    </section>
  );
};

const FactsStrip = () => {
  const { c } = useLang();
  return (
    <section data-testid="at-a-glance-strip" className="py-16 md:py-20 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {c.facts.map((f, i) => {
            const Icon = ICONS[f.icon];
            const col = FACT_COLORS[i % FACT_COLORS.length];
            return (
              <Reveal key={f.title} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -6, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className="rounded-3xl bg-cream border border-bone p-7 h-full"
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: col.bg }}>
                    <Icon className="w-6 h-6" style={{ color: col.fg }} />
                  </div>
                  <h3 className="font-display font-bold text-lg text-forest">{f.title}</h3>
                  <p className="text-sm text-bark mt-1.5 leading-relaxed">{f.desc}</p>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Chapters = () => {
  const { t, c } = useLang();
  return (
    <section className="py-20 md:py-28 bg-forest text-cream relative overflow-hidden grain">
      <div className="absolute -top-40 right-0 w-[30rem] h-[30rem] rounded-full bg-tree/25 blur-3xl" />
      <div className="absolute bottom-0 -left-24 w-72 h-72 rounded-full bg-tangerine/15 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber">{t("chapters_overline")}</p>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl leading-tight">
            {t("chapters_a")} <span className="text-stroke" style={{ WebkitTextStrokeColor: "rgba(250,249,245,0.4)" }}>{t("chapters_b")}</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-14">
          {c.chapters.map((ch, i) => {
            const Icon = ICONS[ch.icon];
            const col = CHAPTER_COLORS[i % CHAPTER_COLORS.length];
            return (
              <Reveal key={ch.num} delay={i * 0.1}>
                <div className="flex gap-6 group">
                  <span className="font-accent font-semibold text-5xl sm:text-6xl leading-none transition-colors" style={{ color: col }}>{ch.num}</span>
                  <div>
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5" style={{ color: col }} />
                      <h3 className="font-display font-bold text-xl sm:text-2xl">{ch.title}</h3>
                    </div>
                    <p className="mt-3 text-cream/70 text-sm sm:text-base leading-relaxed">{ch.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ProgramsPreview = () => {
  const { t, c } = useLang();
  return (
    <section className="py-20 md:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-tangerine">{t("programs_overline")}</p>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-forest tracking-tight">{t("programs_h2")}</h2>
          </div>
          <Link to="/programs" data-testid="programs-preview-link"
                className="group flex items-center gap-2 font-bold text-tree hover:text-tangerine transition-colors">
            {t("programs_link")} <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {c.programs.map((p, i) => {
            const col = FACT_COLORS[i % FACT_COLORS.length];
            return (
              <Reveal key={p.id} delay={i * 0.09}>
                <motion.div whileHover={{ y: -8 }} transition={{ type: "spring", stiffness: 280 }} className="group h-full">
                  <Link to="/programs" data-testid={p.testId} className="block h-full rounded-3xl bg-white border border-bone overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-forest/15 transition-shadow duration-500">
                    <div className="relative h-52 overflow-hidden">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" />
                      <span className="absolute top-4 left-4 rounded-full px-3 py-1 text-[11px] font-bold" style={{ background: col.bg, color: col.fg }}>{p.age}</span>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display font-bold text-lg text-forest group-hover:text-tree transition-colors">{p.title}</h3>
                      <p className="mt-2 text-sm text-bark leading-relaxed">{p.short}</p>
                    </div>
                  </Link>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { t, c } = useLang();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const tmr = setInterval(() => setIdx((i) => (i + 1) % c.testimonials.length), 7000);
    return () => clearInterval(tmr);
  }, [c.testimonials.length]);
  const cur = c.testimonials[idx % c.testimonials.length];

  return (
    <section data-testid="testimonials-carousel" className="py-20 md:py-28 bg-sand/60 relative overflow-hidden">
      <Quote className="absolute top-10 left-8 w-40 h-40 text-tree/5" />
      <div className="max-w-4xl mx-auto px-4 sm:px-8 relative">
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-tangerine">{t("test_overline")}</p>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-forest tracking-tight">{t("test_h2")}</h2>
        </Reveal>
        <div className="mt-12 relative min-h-[18rem] sm:min-h-[14rem]">
          <AnimatePresence mode="wait">
            <motion.figure
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="text-center"
            >
              <blockquote className="text-lg sm:text-xl text-charcoal/90 leading-relaxed font-medium">"{cur.quote}"</blockquote>
              <figcaption className="mt-6">
                <p className="font-display font-bold text-forest">{cur.author}</p>
                <p className="text-xs uppercase tracking-widest text-bark/60 mt-1">{cur.role}</p>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>
        <div className="mt-8 flex items-center justify-center gap-4">
          <button data-testid="testimonial-prev-btn" onClick={() => setIdx((idx - 1 + c.testimonials.length) % c.testimonials.length)}
                  className="w-10 h-10 rounded-full border border-tree/30 flex items-center justify-center text-tree hover:bg-tree hover:text-cream transition-all" aria-label="Previous testimonial">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex gap-2">
            {c.testimonials.map((_, i) => (
              <button key={i} data-testid={`testimonial-dot-${i}`} onClick={() => setIdx(i)} aria-label={`Testimonial ${i + 1}`}
                      className={`h-2 rounded-full transition-all duration-400 ${i === idx % c.testimonials.length ? "w-8 bg-tangerine" : "w-2 bg-tree/30"}`} />
            ))}
          </div>
          <button data-testid="testimonial-next-btn" onClick={() => setIdx((idx + 1) % c.testimonials.length)}
                  className="w-10 h-10 rounded-full border border-tree/30 flex items-center justify-center text-tree hover:bg-tree hover:text-cream transition-all" aria-label="Next testimonial">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

const BlogPreview = () => {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/blog`).then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  if (!posts.length) return null;
  const pick = (p, f) => (lang === "es" && p[`${f}_es`]) || p[f];

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-tangerine">{t("blog_overline")}</p>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-forest tracking-tight">{t("blog_h2")}</h2>
          </div>
          <Link to="/blog" className="group flex items-center gap-2 font-bold text-tree hover:text-tangerine transition-colors">
            {t("blog_link")} <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.09}>
              <Link to={`/blog/${p.slug}`} className="group block rounded-3xl overflow-hidden border border-bone bg-cream hover:shadow-2xl hover:shadow-forest/10 transition-shadow duration-500">
                <div className="h-48 overflow-hidden">
                  <img src={p.cover} alt={pick(p, "title")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-tangerine">{pick(p, "tag")}</span>
                  <h3 className="mt-2 font-display font-bold text-lg text-forest leading-snug group-hover:text-tree transition-colors">{pick(p, "title")}</h3>
                  <p className="mt-2 text-sm text-bark line-clamp-2">{pick(p, "excerpt")}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const TourSection = () => {
  const { t } = useLang();
  return (
    <section id="book-tour" className="py-20 md:py-28 bg-cream grain relative overflow-hidden">
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-sage blur-3xl" />
      <div className="absolute top-10 right-10 w-48 h-48 rounded-full bg-tangerine/10 blur-2xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-tangerine">{t("tour_overline")}</p>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-forest tracking-tight leading-tight">
            {t("tour_h2a")}<br />{t("tour_h2b")}
          </h2>
          <p className="mt-6 text-bark leading-relaxed max-w-md">{t("tour_sub")}</p>
          <div className="mt-8 flex items-center gap-4">
            <img src={IMAGES.preschool} alt="Preschool classroom" className="w-24 h-24 rounded-3xl object-cover rotate-[-3deg] shadow-lg ring-4 ring-[#FDE7E7]" />
            <img src={IMAGES.mixedAge} alt="Children playing together" className="w-24 h-24 rounded-3xl object-cover rotate-[2deg] shadow-lg ring-4 ring-[#FFF2C4] mt-6" />
            <img src={IMAGES.transition} alt="Summer camp fun" className="w-24 h-24 rounded-3xl object-cover rotate-[-2deg] shadow-lg ring-4 ring-[#E3EFFC]" />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-[2rem] bg-white border border-bone p-7 sm:p-10 shadow-2xl shadow-forest/10">
            <TourForm />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { c } = useLang();
  return (
    <>
      <Hero />
      <Marquee items={c.marquee} />
      <FactsStrip />
      <Chapters />
      <ProgramsPreview />
      <Testimonials />
      <BlogPreview />
      <TourSection />
    </>
  );
}
